/**
 * Central auth/session cleanup.
 *
 * Clears everything a signed-in session leaves behind in web storage. Used by
 * the logout menu, the login page's on-mount cleanup, and the 401 handler so
 * the three paths can't drift apart.
 *
 * Note: React in-memory state (merchant model, initialState.currentUser) must
 * be reset by the caller — this is a pure storage-level function.
 */
import { ACTING_AS_STORAGE_KEY } from '@/models/actingAsMerchant';

const TOKEN_KEYS = [
  'aimerpay_token',
  'aimerpay_refresh_token',
  'aimerpay_token_expires',
];

// Per-user keys (aimerpay_merchant_{userId} etc.) are swept by prefix because
// after a full page reload we no longer know the previous user's id.
const LOCAL_STORAGE_PREFIXES = ['aimerpay_merchant_', 'aimerpay_merchants_'];
const SESSION_STORAGE_PREFIXES = ['aimerpay_workspace_entered'];

const removeByPrefix = (storage: Storage, prefixes: string[]) => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && prefixes.some((prefix) => key.startsWith(prefix))) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
};

export function clearAuthState(): void {
  for (const key of TOKEN_KEYS) {
    localStorage.removeItem(key);
  }
  removeByPrefix(localStorage, LOCAL_STORAGE_PREFIXES);

  sessionStorage.removeItem(ACTING_AS_STORAGE_KEY);
  sessionStorage.removeItem('aimerpay_acting_as_merchants_cache');
  removeByPrefix(sessionStorage, SESSION_STORAGE_PREFIXES);
}
